import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart3, TrendingUp, DollarSign, Car, Eye, Heart, Calendar, FileText, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from '../../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PerformanceDashboard = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vehicles/admin/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error(t('perf.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/vehicles/admin/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vehicles_report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('admin.success') + '!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('admin.error'));
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--accent-primary)' }}></div>
      </div>
    );
  }

  if (!stats) {
    return <div>{t('perf.noData')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {t('perf.title')}
          </h2>
          <p style={{ color: 'var(--text-secondary)' }} className="mt-1">
            {t('perf.subtitle')}
          </p>
        </div>
        <Button onClick={handleExportExcel} disabled={exporting}>
          <Download className="w-4 h-4 mr-2" />
          {exporting ? t('perf.exporting') : t('perf.exportExcel')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900">
              <Car className="w-6 h-6 text-blue-600 dark:text-blue-300" />
            </div>
            <div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('perf.totalVehicles')}</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.vehicles.total}</p>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-green-100 dark:bg-green-900">
              <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-300" />
            </div>
            <div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('perf.available')}</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.vehicles.available}</p>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900">
              <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-300" />
            </div>
            <div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('perf.sold')}</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{stats.vehicles.sold}</p>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-emerald-100 dark:bg-emerald-900">
              <DollarSign className="w-6 h-6 text-emerald-600 dark:text-emerald-300" />
            </div>
            <div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('perf.totalSoldValue')}</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                ${stats.sales?.total_sold_value?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-yellow-100 dark:bg-yellow-900">
              <DollarSign className="w-6 h-6 text-yellow-600 dark:text-yellow-300" />
            </div>
            <div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('perf.avgPrice')}</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                ${Math.round(stats.pricing.average).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
            <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{t('perf.customerEngagement')}</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span style={{ color: 'var(--text-secondary)' }}>{t('perf.testDriveRequests')}</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{stats.engagement.test_drives}</span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: 'var(--text-secondary)' }}>{t('perf.pendingTestDrives')}</span>
              <span className="font-semibold text-orange-600">{stats.engagement.pending_test_drives}</span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: 'var(--text-secondary)' }}>{t('perf.priceQuotes')}</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{stats.engagement.price_quotes}</span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: 'var(--text-secondary)' }}>{t('perf.pendingQuotes')}</span>
              <span className="font-semibold text-orange-600">{stats.engagement.pending_quotes}</span>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
            <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{t('perf.pricingStats')}</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span style={{ color: 'var(--text-secondary)' }}>{t('perf.avgPrice')}</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>${Math.round(stats.pricing.average).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: 'var(--text-secondary)' }}>{t('perf.minPrice')}</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>${Math.round(stats.pricing.minimum).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: 'var(--text-secondary)' }}>{t('perf.maxPrice')}</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>${Math.round(stats.pricing.maximum).toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center">
              <span style={{ color: 'var(--text-secondary)' }}>{t('perf.featuredVehicles')}</span>
              <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{stats.vehicles.featured}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Most Viewed Vehicles */}
      <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
        <div className="flex items-center gap-3 mb-4">
          <Eye className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
          <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{t('perf.mostViewed')}</h3>
        </div>
        <div className="space-y-3">
          {stats.top_vehicles.most_viewed.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('perf.noData')}</p>
          ) : (
            stats.top_vehicles.most_viewed.map((vehicle, index) => (
              <div key={vehicle.id} className="flex items-center gap-4 p-3 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
                <span className="font-bold text-lg" style={{ color: 'var(--text-secondary)' }}>#{index + 1}</span>
                {vehicle.images?.[0] && (
                  <img src={vehicle.images[0]} alt={vehicle.title} className="w-20 h-20 object-cover rounded-lg" />
                )}
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {vehicle.title || `${vehicle.year} ${vehicle.brand} ${vehicle.model}`}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>${vehicle.price?.toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{vehicle.view_count}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Most Favorited Vehicles */}
      <div className="p-6 rounded-lg" style={{ background: 'var(--bg-primary)' }}>
        <div className="flex items-center gap-3 mb-4">
          <Heart className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
          <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{t('perf.mostFavorited')}</h3>
        </div>
        <div className="space-y-3">
          {stats.top_vehicles.most_favorited.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('perf.noData')}</p>
          ) : (
            stats.top_vehicles.most_favorited.map((vehicle, index) => (
              <div key={vehicle.id} className="flex items-center gap-4 p-3 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
                <span className="font-bold text-lg" style={{ color: 'var(--text-secondary)' }}>#{index + 1}</span>
                {vehicle.images?.[0] && (
                  <img src={vehicle.images[0]} alt={vehicle.title} className="w-20 h-20 object-cover rounded-lg" />
                )}
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {vehicle.title || `${vehicle.year} ${vehicle.brand} ${vehicle.model}`}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>${vehicle.price?.toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-500" />
                  <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{vehicle.favorite_count}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
